/**
 * Surveillance tab (`ldt-daa`, spec §9):
 *   - Active surveillance services contributing to this drone's eval ring
 *   - Aircraft Agent accordion (mirrors Overview row)
 *   - Auto-Accept Rules accordion (per drone, from inventory)
 */
import type { DroneLive } from '@/lib/store/drones';
import { Section, SectionGroup } from '@/components/Section';
import { InfoRow } from '@/components/InfoRow';
import { Accordion } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Dot } from '@/components/ui/dot';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { aaClass } from '@/lib/store/drones';
import type { BadgeClass } from '@/lib/store/drones';
import { useSurvStore } from '@/lib/store/surv';
import type { SurveillanceService } from '@/lib/mqtt/schemas/surveillance';
import { useInventoryDrones } from '@/lib/api/queries';
import { useState } from 'react';
import { useShallow } from 'zustand/shallow';
import { cn } from '@/lib/utils';

interface ServiceRow {
  id: string;
  label: string;
  sub: string;
  active: { value: string; tone: BadgeClass };
  trust: { value: string; tone: BadgeClass };
  perf: { value: string; tone: BadgeClass };
}

/** Derive the per-drone view by cross-referencing the drone's
 *  `oi.subscribed_services` list with the global `useSurvStore.byId`.
 *  Returns an empty array if either side has no data — that's the
 *  signal for "live mode, awaiting backend". */
function buildServiceRows(
  subscribed: string[],
  byId: Record<string, SurveillanceService>,
): ServiceRow[] {
  return subscribed
    .map((id): ServiceRow | null => {
      const svc = byId[id];
      if (!svc) return null;
      const perfTone: BadgeClass =
        svc.service_state === 'Active' ? 'ok' : svc.service_state === 'Degraded' ? 'warn' : 'crit';
      const trustTone: BadgeClass =
        svc.trust === 'High' ? 'ok' : svc.trust === 'Medium' ? 'warn' : 'muted';
      // "N/A" when the service publishes nothing measurable (e.g. Community
      // open-source feed without active receiver count).
      const activeValue =
        svc.total_sensors > 0 ? `${svc.online_sensors} / ${svc.total_sensors}` : 'N/A';
      const activeTone: BadgeClass =
        svc.total_sensors === 0
          ? 'muted'
          : svc.online_sensors === svc.total_sensors
            ? 'ok'
            : svc.online_sensors === 0
              ? 'crit'
              : 'warn';
      const subLine =
        svc.total_sensors > 0
          ? `${svc.total_sensors} receivers · ${svc.service_state}`
          : `${svc.label.replace('live_', '')} feed · ${svc.service_state}`;
      return {
        id: svc.service_id,
        label: svc.label,
        sub: subLine,
        active: { value: activeValue, tone: activeTone },
        trust: { value: svc.trust, tone: trustTone },
        perf: { value: svc.service_state, tone: perfTone },
      };
    })
    .filter((r): r is ServiceRow => r !== null);
}

export function SurveillanceTab({ drone }: { drone: DroneLive }) {
  const subscribed = drone.oi?.subscribed_services ?? [];
  const byId = useSurvStore(useShallow((s) => s.byId));
  const services = buildServiceRows(subscribed, byId);
  const c2 = drone.c2;
  const aaTone = aaClass(c2?.aa_agent_status ?? null, c2?.aa_latency_ms ?? null);

  return (
    <SectionGroup>
      <Section title="Active Services">
        <p className="text-[0.692em] text-(--color-text3) -mt-1 mb-1 leading-snug">
          These services are contributing to airspace surveillance for this aircraft within its Evaluation ring.
        </p>
        {services.length === 0 ? (
          <div className="text-[0.769em] text-(--color-text3) text-center py-4 rounded-md border border-dashed border-(--color-border)">
            {subscribed.length === 0
              ? 'No services subscribed for this aircraft.'
              : 'Awaiting service telemetry…'}
          </div>
        ) : (
          <div className="space-y-1.5">
            {services.map((s) => (
              <ServiceCard key={s.id} row={s} />
            ))}
          </div>
        )}
      </Section>

      <Section title="Aircraft Agent">
        <Accordion
          title={c2?.aa_agent_id ?? '—'}
          meta={
            <span className="flex items-center gap-1.5">
              <Dot variant={aaTone === 'muted' ? 'muted' : aaTone} size="xs" />
              <span className={cn('font-bold', aaTone === 'ok' && 'text-(--color-green)', aaTone === 'warn' && 'text-(--color-amber)', aaTone === 'crit' && 'text-(--color-red)')}>{c2?.aa_agent_status ?? '—'}</span>
              <span>{c2?.aa_latency_ms != null ? `${c2.aa_latency_ms} ms` : ''}</span>
            </span>
          }
        >
          <InfoRow label="Last Seen" value={drone.received_at_ms ? new Date(drone.received_at_ms).toUTCString().slice(17, 25) : '—'} mono />
          <InfoRow
            label="Acceptance Mode"
            value={c2?.aa_acceptance_mode ?? '—'}
            valueClassName={
              c2?.aa_acceptance_mode === 'Auto'
                ? 'text-(--color-green)'
                : c2?.aa_acceptance_mode === 'Conditional Auto'
                  ? 'text-(--color-amber)'
                  : c2?.aa_acceptance_mode === 'Manual'
                    ? 'text-(--color-text2)'
                    : 'text-(--color-text3)'
            }
          />
        </Accordion>
      </Section>

      <AutoAcceptSection droneId={drone.id} />
    </SectionGroup>
  );
}

function ServiceCard({ row }: { row: ServiceRow }) {
  const [agl, setAgl] = useState<string>('off');
  return (
    <article className="rounded-md border border-(--color-border) bg-(--color-surface) p-2.5 space-y-2">
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[0.846em] font-bold">{row.label}</div>
          <div className="text-[0.692em] text-(--color-text3) font-mono">{row.sub}</div>
        </div>
        <Select value={agl} onValueChange={setAgl}>
          <SelectTrigger className="w-[120px] shrink-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="off">Coverage off</SelectItem>
            <SelectItem value="75">75m AGL</SelectItem>
            <SelectItem value="150">150m AGL</SelectItem>
            <SelectItem value="300">300m AGL</SelectItem>
          </SelectContent>
        </Select>
      </header>
      <div className="grid grid-cols-3 gap-2 text-center">
        <Metric label="Active Sensors" value={row.active.value} tone={row.active.tone} />
        <Metric label="Trust Level" value={row.trust.value} tone={row.trust.tone} />
        <Metric label="Performance" value={row.perf.value} tone={row.perf.tone} />
      </div>
    </article>
  );
}

function Metric({ label, value, tone }: { label: string; value: string; tone: BadgeClass }) {
  return (
    <div>
      <div
        className={cn(
          'text-[0.846em] font-bold',
          tone === 'ok' && 'text-(--color-green)',
          tone === 'warn' && 'text-(--color-amber)',
          tone === 'crit' && 'text-(--color-red)',
          tone === 'muted' && 'text-(--color-text3)',
        )}
      >
        {value}
      </div>
      <div className="text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3) mt-0.5">{label}</div>
    </div>
  );
}

function AutoAcceptSection({ droneId }: { droneId: string }) {
  const { data, isPending, isError } = useInventoryDrones();
  const inv = data?.find((d) => d.drone_id === droneId);

  return (
    <Section title="Auto-Accept Rules">
      {isPending && <Skeleton className="h-20 w-full" />}
      {isError && <div className="text-[0.769em] text-(--color-red)">Failed to load rules.</div>}
      {inv && (
        <Accordion
          title={`${inv.daa_rules.length} rules configured`}
          meta={
            <span>{inv.daa_rules.filter((r) => r.enabled).length} auto · {inv.daa_rules.filter((r) => !r.enabled).length} manual</span>
          }
          defaultOpen
        >
          <div className="space-y-2">
            {inv.daa_rules.length === 0 && <div className="text-[0.769em] text-(--color-text3)">No rules configured.</div>}
            {inv.daa_rules.map((r, idx) => {
              const tone: BadgeClass = r.priority === 'Critical' ? 'crit' : r.priority === 'High' ? 'warn' : 'muted';
              return (
                <article
                  key={r.rule_id}
                  className={cn(
                    'rounded-md border border-(--color-border) bg-(--color-bg) p-2',
                    tone === 'crit' && 'bg-(--color-red-bg)/40 border-(--color-red)/30',
                    tone === 'warn' && 'bg-(--color-amber-bg)/40 border-(--color-amber)/30',
                  )}
                >
                  <div className={cn('text-[0.769em] font-bold mb-1', tone === 'crit' && 'text-(--color-red)', tone === 'warn' && 'text-(--color-amber)')}>
                    Rule {idx + 1} — {r.priority}
                  </div>
                  <InfoRow label="Condition" value={r.condition} />
                  <InfoRow label="Result" value={r.result} valueClassName={tone === 'warn' ? 'text-(--color-amber)' : 'text-(--color-green)'} />
                  <div className="flex justify-between text-[0.615em] text-(--color-text3) mt-1">
                    <span>{r.enabled ? 'Enabled' : 'Disabled'}</span>
                    <Badge variant={r.enabled ? 'ok' : 'muted'}>{r.enabled ? 'Active' : 'Off'}</Badge>
                  </div>
                </article>
              );
            })}
          </div>
        </Accordion>
      )}
    </Section>
  );
}
